import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import FiltersBar from "./FiltersBar";
import LocationsTable from "./LocationsTable";
import LocationMap from "./LocationMap";
import { getLocationsData } from "../../utils/api";

export default function LocationsPage() {
  const [filters, setFilters] = useState({
    q: "",
    status: [],
    city: [],
    sensor: [],
  });
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());
  const [timezone, setTimezone] = useState(localStorage.getItem('crocus_timezone') || 'UTC');
  const navigate = useNavigate();
  
  const timeRef = useRef(null);

  // Listen for timezone changes from header
  useEffect(() => {
    const handleTimezoneChange = (e) => {
      setTimezone(e.detail);
    };
    
    window.addEventListener('timezoneChange', handleTimezoneChange);
    return () => window.removeEventListener('timezoneChange', handleTimezoneChange);
  }, []);

  // Fetch locations from API
  useEffect(() => {
    setLoading(true);
    getLocationsData()
      .then(data => {
        setLocations(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);
  
  // Update time every 5 seconds with timezone
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      let timeString;
      
      if (timezone === 'UTC') {
        timeString = now.toLocaleTimeString('en-US', { timeZone: 'UTC', hour12: false });
      } else {
        timeString = now.toLocaleTimeString('en-US', { timeZone: timezone });
      }
      
      setCurrentTime(timeString);
      if (timeRef.current) {
        timeRef.current.textContent = timeString;
      }
    };
    
    updateTime(); // Initial update
    const interval = setInterval(updateTime, 5000);
    return () => clearInterval(interval);
  }, [timezone]);

  const filtered = useMemo(() => {
    let arr = locations;
    
    if (filters.q) {
      const q = filters.q.toLowerCase();
      arr = arr.filter((n) => (n.name + n.city).toLowerCase().includes(q));
    }
    
    // Filter by status - include if ANY selected status matches
    if (filters.status.length > 0) {
      arr = arr.filter((n) => filters.status.includes(n.status));
    }
    
    // Filter by city - include if ANY selected city matches
    if (filters.city.length > 0) {
      arr = arr.filter((n) => filters.city.includes(n.city));
    }
    
    // Filter by sensor - include if ANY selected sensor type matches
    if (filters.sensor.length > 0) {
      arr = arr.filter((n) => {
        return filters.sensor.some(sensor => {
          if (sensor === "mfr") return n.mfrNodes?.length > 0;
          if (sensor === "sap") return n.sapFlowMeters?.length > 0;
          if (sensor === "band") return n.devices?.some(d => d.type === "Band Dendrometer");
          if (sensor === "wxt") return n.devices?.some(d => d.type === "WXT536 Sensor");
          if (sensor === "aqt") return n.devices?.some(d => d.type === "AQT530 Sensor");
          return false;
        });
      });
    }
    
    return arr;
  }, [locations, filters]);

  const total = filtered.length;
  
  const handleDownloadCSV = () => {
    const headers = ['Location', 'Full Name', 'City', 'Latitude', 'Longitude', 'MFR Nodes', 'Sap Flow Meters', 'Band Dendrometers', 'WXT536 Sensors', 'AQT530 Sensors'];
    
    const csvContent = [
      headers.join(','),
      ...filtered.map(loc => {
        const bandDendrometers = loc.devices?.filter(d => d.type === "Band Dendrometer").map(d => d.serialNumber) || [];
        const wxt536Sensors = loc.devices?.filter(d => d.type === "WXT536 Sensor").map(d => d.serialNumber) || [];
        const aqt530Sensors = loc.devices?.filter(d => d.type === "AQT530 Sensor").map(d => d.serialNumber) || [];
        return [
          loc.name,
          loc.fullName,
          loc.city,
          loc.coordinates[0],
          loc.coordinates[1],
          loc.mfrNodes.join('; '),
          loc.sapFlowMeters.join('; '),
          bandDendrometers.join('; '),
          wxt536Sensors.join('; '),
          aqt530Sensors.join('; ')
        ].map(v => `"${v}"`).join(',');
      })
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crocus-locations-${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-center p-12">
          <CircularProgress />
          <span className="ml-3">Loading locations...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <Alert severity="error">
          Failed to load locations: {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            {total} {total === 1 ? 'Location' : 'Locations'}{" "}
            <small className="text-gray-500">| <span ref={timeRef}>{currentTime}</span> ({timezone === 'UTC' ? 'UTC' : timezone.split('/')[1]})</small>
          </h2>
        </div>

        <LocationMap
          key="location-map"
          locations={filtered}
          onLocationClick={(location) => navigate(`/location/${location.name}`)}
        />

        <FiltersBar
          locations={locations}
          filters={filters}
          setFilters={setFilters}
          onDownload={handleDownloadCSV}
        />

        <LocationsTable
          locations={filtered}
          onLocationClick={(location) => navigate(`/location/${location.name}`)}
        />
      </div>
    </div>
  );
}